import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import MemberProductCard from './memberProductCard';

@observer
class MemberProducts extends Component {
    constructor(props) {
        super(props);
        this.state = {
            products: [],
            loading: true,
            addedToCart: {},
            showProducts: !!props.startExpanded,
            initialized: false,
        };
    }

    loadProducts = async () => {
        const { productStore } = this.props.stores;
        const { customer, convention, onCount } = this.props;
        if (!customer || !convention?.id) {
            this.setState({ loading: false });
            if (onCount) {
                onCount(0);
            }
            return;
        }
        // Participant-gated route scoped to the CONVENTION's customer. The generic
        // /api/products/ list is scoped to the caller's own user.customer, so members
        // of other clubs (or with no club) got an empty list there.
        const memberProducts = await productStore.load({
            url: `/api/conventions/${convention?.id}/member-products`,
            skipFilter: true,
            addData: [],
            limit: 50,
            skipUpdate: true,
        });

        let products = memberProducts || [];

        // Also load the signup product if it exists and isn't already in the list
        if (convention?.signupProduct > 0) {
            const signupProductAlreadyLoaded = products.find(p => p.id === convention.signupProduct);
            if (!signupProductAlreadyLoaded) {
                const signupProducts = await productStore.load({
                    query: { id: convention.signupProduct },
                    url: '/api/products/public',
                    addData: [],
                    skipUpdate: true,
                });
                const signupProduct = signupProducts?.find(p => p.id === convention.signupProduct);
                if (signupProduct) {
                    products = [signupProduct, ...products];
                }
            }
        }

        this.setState({
            products,
            loading: false,
            showProducts: !!this.props.startExpanded,
            initialized: true,
        });

        if (onCount) {
            onCount(products.length);
        }
    }

    addToCart = async (product) => {
        const { userStore } = this.props.stores;
        const { convention } = this.props;
        if (product) {
            await userStore.addToCart({
                product: product.id,
                qty: 1,
                conventionId: convention?.id,
            });
            this.setState(prev => ({
                addedToCart: { ...prev.addedToCart, [product.id]: true },
            }));
        }
    }

    toggleProducts = () => {
        this.setState(prev => ({ showProducts: !prev.showProducts }));
    }

    componentDidMount() {
        this.loadProducts();
        // Ensure cart data is loaded (may not be in user object on page reload)
        const { userStore } = this.props.stores;
        userStore.loadCart();
    }

    render() {
        const { products, loading, addedToCart, showProducts } = this.state;
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        const { cart = [] } = user;
        const { convention } = this.props;

        if (loading) {
            return (
                <div class='text-center py-3'>
                    <i class='fa-solid fa-spinner fa-spin' />
                </div>
            );
        }

        if (!products || products.length === 0) {
            return null;
        }

        // Signup product first, then the rest. Always a grid, never a horizontal scroller.
        const hasSignupProduct = convention?.signupProduct > 0;
        const signupProduct = hasSignupProduct ? products.find(p => p.id === convention.signupProduct) : null;
        const otherProducts = signupProduct ? products.filter(p => p.id !== convention.signupProduct) : products;
        const orderedProducts = signupProduct ? [signupProduct, ...otherProducts] : otherProducts;
        const renderOpts = { darkmode, cart, addedToCart, convention, compact: false };

        return (<>
            <div
                id='convention-member-products'
                class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1 mt-3'
                onClick={this.toggleProducts}
                style='cursor: pointer;'
            >
                <span>
                    <i class={`fa-duotone ${showProducts ? 'fa-chevron-down' : 'fa-chevron-right'} me-2`} />
                    <i class='fa-duotone fa-bag-shopping me-2' />
                    <Text id='convention.member-products'>Products</Text>
                    <span class='badge bg-secondary ms-2'>{products.length}</span>
                </span>
            </div>

            {showProducts && (
                <div class={`mt-0 mb-1 mx-3 px-2 py-2 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                    <div class='row g-2'>
                        {orderedProducts.map(product => (
                            <MemberProductCard key={product.id} {...renderOpts} product={product} onAddToCart={this.addToCart} />
                        ))}
                    </div>
                </div>
            )}
        </>);
    }
}

export default MemberProducts;
